/**
 * `A` — place an anchor (§9.2 `anchor.placing`, §4.3.1 `Anchor`).
 *
 * Click sets `at` (where the thing is); dragging away from it sets `stand`
 * (where a character stands to use it) and the facing is derived from the
 * direction of the drag. A plain click without a drag leaves `stand`
 * undefined and the kind's `stand_offset` fills it in on save.
 */
import type { Facing } from '@/schema'

import { distPct, idle, none, withState, type ToolEffect, type ToolHandler, type ToolResult } from './types'

/** Below this the drag was a click and there is no explicit stand point. */
export const MIN_STAND_DRAG_PCT = 0.8

/** Facing a character has when it walks from `stand` to `at`. */
export function facingFromDelta(dx: number, dy: number): Facing {
  if (Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6) return 'camera'
  if (Math.abs(dx) >= Math.abs(dy) * 2) return dx > 0 ? 'right' : 'left'
  if (Math.abs(dy) >= Math.abs(dx) * 2) return dy > 0 ? 'camera' : 'away'
  if (dy > 0) return dx > 0 ? '3q_right' : '3q_left'
  return dx > 0 ? 'right' : 'left'
}

export const anchorTool: ToolHandler = {
  id: 'anchor',
  shortcut: 'a',
  icon: 'MapPin',
  label: 'Spot',
  hint: 'Click a thing characters use — a door, a bed, a chair. Drag to say where they stand.',
  cursor: 'crosshair',

  reduce(machine, ev, cfg): ToolResult | null {
    const st = machine.state

    if (ev.t === 'pointerdown' && ev.button === 0) {
      if (ev.hit.id && machine.state.t === 'idle' && ev.mod) {
        // Ctrl+click an existing anchor moves it rather than placing a new one.
        return idle('anchor', [{ k: 'beginGesture' }, { k: 'moveAnchor', id: ev.hit.id, at: ev.at }, { k: 'endGesture' }])
      }
      return withState('anchor', { t: 'anchor.placing', kind: cfg.activeKind.anchor ?? '', at: ev.at })
    }

    if (ev.t === 'pointermove') {
      if (st.t !== 'anchor.placing' || !st.at) return none(machine)
      const dx = st.at[0] - ev.at[0]
      const dy = st.at[1] - ev.at[1]
      const stand = distPct(st.at, ev.at) >= MIN_STAND_DRAG_PCT ? ev.at : undefined
      return withState('anchor', { ...st, stand, facing: stand ? facingFromDelta(dx, dy) : undefined })
    }

    if (ev.t === 'pointerup') {
      if (st.t !== 'anchor.placing' || !st.at) return none(machine)
      if (!st.kind) {
        return idle('anchor', [{ k: 'toast', message: 'Pick what kind of spot this is first (the list on the right).', tone: 'info' }])
      }
      const effects: ToolEffect[] = [
        { k: 'beginGesture' },
        { k: 'createAnchor', kind: st.kind, at: st.at, stand: st.stand, facing: st.facing },
        { k: 'endGesture' },
      ]
      return idle('anchor', effects)
    }

    if (ev.t === 'cancel' || ev.t === 'pointercancel') return idle('anchor')

    return null
  },
}
